import { t } from "@lingui/macro";
import {
  CreateTagDocument,
  type CreateTagMutation,
  type CreateTagMutationVariables,
  DeleteTagDocument,
  type DeleteTagMutation,
  type DeleteTagMutationVariables,
  GetTagsDocument,
  type GetTagsQuery,
  type GetTagsQueryVariables,
  type TagFragment,
  UpdateTagDocument,
  type UpdateTagMutation,
  type UpdateTagMutationVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { action, computed, makeObservable, observable } from "mobx";

export class TagStore {
  private fetcher = new FetchHelper<GetTagsQuery, GetTagsQueryVariables>(
    GetTagsDocument,
  );

  private createMutator = new MutationHelper<
    CreateTagMutation,
    CreateTagMutationVariables
  >(CreateTagDocument);

  private updateMutator = new MutationHelper<
    UpdateTagMutation,
    UpdateTagMutationVariables
  >(UpdateTagDocument);

  private deleteMutator = new MutationHelper<
    DeleteTagMutation,
    DeleteTagMutationVariables
  >(DeleteTagDocument);

  @observable updatingTagIds = new Set<string>();

  @observable.ref tags: TagFragment[] = [];

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @computed get isLoading() {
    return this.fetcher.isLoading.value;
  }

  @computed get isCreating() {
    return this.createMutator.isLoading.value;
  }

  @action.bound async fetchTags() {
    const [data, error] = await this.fetcher.fetch();

    if (error || !data.getTags) {
      return;
    }

    this.tags = data.getTags;
  }

  @action.bound async createTag(title: string) {
    const [data, error] = await this.createMutator.mutate({ title });

    if (error) return;

    if (!data?.createTag) {
      this.appStore.UIStore.toast({
        title: t`Error creating tag`,
        status: "error",
      });
      return;
    }

    this.tags = this.tags.concat(data.createTag);
    this.appStore.UIStore.toast({
      status: "success",
      title: t`Tag successfully created`,
    });

    return data.createTag;
  }

  @action.bound async updateTag(id: string, title: string) {
    this.updatingTagIds.add(id);
    const [data, error] = await this.updateMutator.mutate({ id, title });
    this.updatingTagIds.delete(id);

    if (error) return;

    if (!data?.updateTag) {
      this.appStore.UIStore.toast({
        title: t`Error updating tag`,
        status: "error",
      });
      return;
    }

    const updatedTag = data.updateTag;
    this.tags = this.tags.map((tag) => (tag.id === id ? updatedTag : tag));

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Tag successfully updated`,
    });

    return updatedTag;
  }

  @action.bound async deleteTag(id: string) {
    this.updatingTagIds.add(id);
    const [data, error] = await this.deleteMutator.mutate({ id });
    this.updatingTagIds.delete(id);

    if (error) return false;

    if (!data?.deleteTag) {
      this.appStore.UIStore.toast({
        title: t`Error deleting tag`,
        status: "error",
      });
      return false;
    }

    this.tags = this.tags.filter((tag) => tag.id !== id);
    return true;
  }
}
